import { FC } from 'react';
import {
  Box,
  VStack,
  Text,
  useColorModeValue,
  HStack,
  Badge,
  Divider,
} from '@chakra-ui/react';
import { useDroppable } from '@dnd-kit/core';
import {
  SortableContext,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { FunnelStep, LeadWithFunnel } from '../../types';
import { LeadCard } from './LeadCard';

export interface FunnelStepCardProps {
  step: FunnelStep;
  leads: LeadWithFunnel[];
  onLeadClick?: (lead: LeadWithFunnel) => void;
  stepId: string;
}

export const FunnelStepCard: FC<FunnelStepCardProps> = ({
  step,
  leads,
  onLeadClick,
  stepId,
}) => {
  const cardBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const isOverBorder = useColorModeValue('blue.400', 'blue.300');
  const isOverBg = useColorModeValue('blue.50', 'blue.900');
  const headerGradient = step.color
    ? `linear-gradient(135deg, ${step.color}, ${step.color}CC)`
    : useColorModeValue('linear-gradient(135deg, #2563EB, #1D4ED8)', 'linear-gradient(135deg, #2563EB, #1D4ED8)');
  const headerTextColor = useColorModeValue('whiteAlpha.900', 'whiteAlpha.900');
  const totalDealValue = leads.reduce((sum, lead) => {
    const value = lead.deal_value ? Number.parseFloat(lead.deal_value) : 0;
    return Number.isNaN(value) ? sum : sum + value;
  }, 0);
  const formattedTotalDealValue = totalDealValue > 0
    ? totalDealValue.toLocaleString(undefined, { style: 'currency', currency: 'USD' })
    : null;

  const { setNodeRef, isOver } = useDroppable({
    id: stepId,
  });

  return (
    <Box
      ref={setNodeRef}
      minW="280px"
      maxW="320px"
      bg={isOver ? isOverBg : cardBg}
      border="1px"
      borderColor={isOver ? isOverBorder : borderColor}
      borderRadius="lg"
      p={4}
      boxShadow={isOver ? 'lg' : 'base'}
      transition="all 0.2s ease"
      position="relative"
      _before={{
        content: '""',
        position: 'absolute',
        inset: 0,
        borderRadius: 'inherit',
        bg: isOver ? isOverBg : 'transparent',
        opacity: 0.6,
        pointerEvents: 'none',
        transition: 'opacity 0.2s ease',
      }}
    >
      <VStack align="stretch" spacing={3}>
        <Box
          bgGradient={headerGradient}
          borderRadius="md"
          px={4}
          py={3}
          color={headerTextColor}
          boxShadow="sm"
        >
          <VStack align="start" spacing={1}>
            <Text fontSize="xs" textTransform="uppercase" letterSpacing="0.2em" opacity={0.7}>
              Stage
            </Text>
            <HStack justify="space-between" w="full">
              <VStack align="start" spacing={0}>
                <Text fontSize="lg" fontWeight="semibold">
                  {step.name}
                </Text>
                <Text fontSize="xs" opacity={0.85}>
                  {leads.length} {leads.length === 1 ? 'lead' : 'leads'}
                </Text>
              </VStack>
              {formattedTotalDealValue && (
                <Badge
                  colorScheme="blackAlpha"
                  bg='whiteAlpha.200'
                  variant="subtle"
                  borderRadius="full"
                  px={3}
                  py={1}
                  fontWeight="semibold"
                  fontSize="xs"
                >
                  {formattedTotalDealValue}
                </Badge>
              )}
            </HStack>
          </VStack>
        </Box>
        <Divider borderColor={useColorModeValue('gray.100', 'gray.700')} />
        <SortableContext
          items={leads.map(l => l.id.toString())}
          strategy={verticalListSortingStrategy}
        >
          <VStack
            align="stretch"
            spacing={3}
            maxH="600px"
            overflowY="auto"
            pr={1}
            sx={{
              '&::-webkit-scrollbar': {
                width: '6px',
              },
              '&::-webkit-scrollbar-thumb': {
                background: useColorModeValue('#CBD5F5', '#1F2937'),
                borderRadius: 'full',
              },
            }}
          >
            {leads.map((lead) => (
              <LeadCard
                key={lead.id}
                lead={lead}
                onClick={onLeadClick}
                variant="compact"
                draggable
                accentColor={step.color}
                campaignLabel={lead.campaign_link_id ? `#${lead.campaign_link_id}` : undefined}
                showCampaign
              />
            ))}
          </VStack>
        </SortableContext>
      </VStack>
    </Box>
  );
};
